import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createMessage } from './utility/createMessage';
import { finalMessage, greetingMessage, kraTable, notEligible, rules } from './utility/messages';

const initialState = {
  desiredDesignation: '',
  currentDesignation: '',
  desiredDesignationPreview: '',
  currentDesignationPreview: '',
  designationType: '',
  eligible: true,
  eligibleMessage: '',
  current: { designation: 'current', title: 0, question: 0 },
  messages: [],
  kras: {
    current: [],
    desired: [],
  },
  preview: { current: {}, desired: {} },
  juryDetails: null,
  reason: null,
  editForChanges: false,
  completed: { current: {}, desired: {} }, //key:kra title , value : array of response objects,
  draft: {},
  usingDraft: false,
  draftForm: null,
  giftForms: { my: {}, all: { pending: {}, previous: {} }, junior: { pending: {}, previous: {} } },
  viewForm: false,
  viewPrefilledForm: false,
  disableInput: true,
  botTyping: false,
  fullScreen: false,
  editPreview: false,
  isNext: true,
  isStarted: false,
  showMyHistory: false,
  showSubmittedHistory: false,
  showAdminHistory: false,
  showToolbar: true,
};

export const sendIncomingMessage = createAsyncThunk('gift/sendIncomingMessage', async (payload, { getState }) => {
  let { message = false, duration = 1200, allowInput = false } = payload;
  //if custom message then return immediately
  if (message) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({ message: message, allowInput, isStarted: true });
      }, duration);
    });
  }
  const state = getState();
  const designations = selectDesgination(state);
  const current = selectCurrent(state);
  const isNext = selectIsNext(state);
  const isStarted = selectIsStarted(state);
  const [title, question] = selectCurrentQuestion(state);
  let totalQuestions = state.gift.kras[current.designation][current.title].questions.length;
  let incoming = [];

  //if its the first question of the current designation
  if (isNext && current.title === 0 && current.question === 0) {
    let displayKra = kraTable(state.gift.kras[current.designation]);
    incoming.push(
      createMessage(
        <b style={{ color: '#0A4FAC' }}>
          Your {current.designation} designation is {designations[current.designation]}
          <br />
          We will start with KRAS with respect to your {current.designation} designation
        </b>
      )
    );
    incoming = incoming.concat(displayKra);
  }
  //if its the first question of the title then add the title of the kra
  if (isNext && current.question === 0) {
    incoming.push(
      createMessage(
        <b>
          Questions regarding {title} - [{totalQuestions}]
        </b>
      )
    );
  }
  //if there is question pending then send the new question
  if (isNext)
    incoming.push(
      createMessage(question, {
        select: true,
        designation: current.designation,
        title: title,
        question: current.question,
        totalQuestions: totalQuestions,
      })
    );

  //if there are no questions left
  if (!isNext) {
    incoming.push(createMessage(finalMessage));
    duration = 2000;
  }
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ message: incoming, allowInput, isStarted });
    }, duration);
  });
});
export const giftSlice = createSlice({
  name: 'gift',
  initialState: initialState,
  reducers: {
    setKras: (state, action) => {
      const { eligible, message } = action.payload;
      //send greeting message
      state.messages = [];
      let greet = createMessage(greetingMessage);
      state.messages.push(greet);

      if (!eligible) {
        state.eligible = false;
        state.eligibleMessage = message;
        //send not eligible message
        let notEligibleMessage = createMessage(notEligible(message));
        state.messages.push(notEligibleMessage);
      } else {
        const { current, desired, draftForm } = action.payload.data;
        state.draftForm = draftForm;
        state.currentDesignation = current.designation;
        state.desiredDesignation = desired.designation;
        state.currentDesignationPreview = current.designation;
        state.desiredDesignationPreview = desired.designation;
        state.designationType = current.designation.split(' - ')[1];
        //setting current kras
        Object.keys(current.kras).forEach(item => {
          let temp = item.split('_').join(' ');
          let title = temp.charAt(0).toUpperCase() + temp.slice(1);
          state.kras.current.push({ title: title, questions: current.kras[item] });
        });

        //setting desired kras
        Object.keys(desired.kras).forEach(item => {
          let temp = item.split('_').join(' ');
          let title = temp.charAt(0).toUpperCase() + temp.slice(1);
          state.kras.desired.push({ title: title, questions: desired.kras[item] });
        });

        //send the rules message
        let ruleMessage = createMessage(rules);
        let startButton = createMessage('', { startButton: true });
        state.messages = state.messages.concat([ruleMessage, startButton]);
      }
    },
    setGiftForms: (state, action) => {
      const { forms, key, replace = false } = action.payload;
      let allForms = [];
      if (replace) {
        state.giftForms[key] = { pending: {}, previous: {} };
        let pending = forms.pending;
        for (let i = 0; i < pending.length; i++) {
          state.giftForms[key].pending[pending[i]._id] = pending[i].forms;
        }

        let previous = forms.previous;
        for (let i = 0; i < previous.length; i++) {
          state.giftForms[key].previous[previous[i]._id] = previous[i].forms;
        }

        return;
      }
      for (let i = 0; i < forms.length; i++) {
        state.giftForms[key][forms[i]._id] = forms[i].forms;
        allForms = [...allForms, ...forms[i].forms];
      }
      state.giftForms[key]['All'] = allForms;
    },
    reset: (state, action) => {
      const { all = false } = action.payload;
      state.current = initialState.current;
      state.draft = {};
      state.viewPrefilledForm = false;
      state.preview = null;
      state.messages.splice(all ? 0 : 3);
      state.completed = initialState.completed;
      state.botTyping = false;
      state.disableInput = true;
      state.isNext = true;
      state.isStarted = false;
      state.draftForm = null;
      state.totalQuestions = 0;
      state.completedQuestions = 0;
      state.editForChanges = false;
      state.showToolbar = true;
    },
    setAnswer: (state, action) => {
      const { answer } = action.payload;
      let titleNumber = state.current.title;
      let questionNumber = state.current.question;
      let designation = state.current.designation;
      let title = state.kras[designation][titleNumber].title;
      let responseObject = {
        question: state.kras[designation][titleNumber].questions[questionNumber],
        answer,
        reason: '',
      };
      if (answer !== 'yes') {
        state.completed[designation][title] = [...(state.completed[designation][title] || []), responseObject];
        state.preview = state.completed;
        //check if there are some questions left in current title
        const questionLength = state.kras[designation][titleNumber].questions.length;
        const kraLength = state.kras[designation].length;
        if (questionLength - 1 === questionNumber) {
          state.current.question = 0;
          // if the kras for current designation are over then change designation to desired
          if (kraLength - 1 === titleNumber) {
            if (state.current.designation === 'desired') {
              state.isNext = false;
            } else {
              state.current.designation = 'desired';
              state.current.title = 0;
            }
          } else {
            state.current.title++;
          }
        } else {
          state.current.question++;
        }
      }
    },
    setReason: (state, action) => {
      const { reason } = action.payload;
      let titleNumber = state.current.title;
      let questionNumber = state.current.question;
      let designation = state.current.designation;
      let title = state.kras[designation][titleNumber].title;
      let responseObject = {
        question: state.kras[designation][titleNumber].questions[questionNumber],
        answer: 'yes',
        reason,
      };
      state.completed[designation][title] = [...(state.completed[designation][title] || []), responseObject];
      state.preview = state.completed;
      const questionLength = state.kras[designation][titleNumber].questions.length;
      const kraLength = state.kras[designation].length;
      if (questionLength - 1 === questionNumber) {
        state.current.question = 0;
        // if the kras for current designation are over then change designation to desired
        if (kraLength - 1 === titleNumber) {
          if (state.current.designation === 'desired') {
            state.isNext = false;
          } else {
            state.current.designation = 'desired';
            state.current.title = 0;
          }
        } else {
          state.current.title++;
        }
      } else {
        state.current.question++;
      }
    },
    sendOutgoingMessage: (state, action) => {
      const { message } = action.payload;
      state.messages.push(message);
    },
    setFullScreen: (state, action) => {
      const { value } = action.payload;
      state.fullScreen = value;
    },
    setEditPreview: (state, action) => {
      const { value } = action.payload;
      state.editPreview = value;
      if (value === true) {
        state.draft = state.preview;
      }
    },
    setDraftAnswer: (state, action) => {
      const { answer, designation, title, question } = action.payload;
      state.draft[designation][title][question] = {
        ...state.draft[designation][title][question],
        answer: answer,
        reason: '',
      };
    },
    setDraftReason: (state, action) => {
      const { reason, designation, title, question } = action.payload;
      state.draft[designation][title][question] = {
        ...state.draft[designation][title][question],
        reason: reason,
      };
    },
    saveDraft: state => {
      state.preview = state.draft;
      if (!state.viewPrefilledForm) {
        state.completed = state.draft;
      }
    },

    setPreview: (state, action) => {
      const {
        my = false,
        reset = false,
        edit = false,
        response,
        currentDesignation,
        desiredDesignation,
        interview = null,
        reason = null,
        showToolbar = true,
      } = action.payload;
      if (my) {
        state.preview = state.completed;
        state.currentDesignationPreview = state.currentDesignation;
        state.desiredDesignationPreview = state.desiredDesignation;
        state.viewPrefilledForm = false;
        state.showToolbar = true;
        return;
      }
      if (reset) {
        state.preview = null;
        state.viewPrefilledForm = false;
        state.interview = null;
        state.reason = null;
        state.editForChanges = false;
        state.showToolbar = true;

        return;
      }
      state.preview = response;
      state.currentDesignationPreview = currentDesignation;
      state.desiredDesignationPreview = desiredDesignation;
      state.viewPrefilledForm = true;
      state.reason = reason;
      state.showToolbar = showToolbar;
      state.juryDetails = interview;
      if (edit) {
        state.editForChanges = true;
      }
    },
    setShowHistory: (state, action) => {
      const { value } = action.payload;
      if (value === 'mygift') {
        state.showMyHistory = !state.showMyHistory;
      } else if (value === 'submitted') {
        state.showSubmittedHistory = !state.showSubmittedHistory;
      } else {
        state.showAdminHistory = !state.showAdminHistory;
      }
    },
    setDraftForm: (state, action) => {
      const response = state.draftForm.response;
      state.completed = { ...state.completed, ...response };
      state.preview = { ...state.completed, ...response };
      state.current = state.draftForm.draft.current;
      state.usingDraft = true;
    },
    deleteDraft: (state, action) => {
      state.usingDraft = false;
      state.draftForm = null;
    },
  },
  extraReducers (builder) {
    builder
      .addCase(sendIncomingMessage.pending, (state, action) => {
        state.disableInput = true;
        state.botTyping = true;
      })
      .addCase(sendIncomingMessage.fulfilled, (state, action) => {
        const { message, allowInput, isStarted } = action.payload;
        state.messages = state.messages.concat(message);
        state.botTyping = false;
        if (allowInput) state.disableInput = false;
        if (!isStarted) {
          state.preview = state.completed;
          state.isStarted = true;
        }
      })
      .addCase(sendIncomingMessage.rejected, (state, action) => {
        console.log(action.error);
      });
  },
});

export default giftSlice.reducer;
export const selectCurrentQuestion = state => {
  let current = state.gift.current;
  let title = current.title;
  let designation = current.designation;
  let questions = state.gift.kras[designation][title];
  return [questions.title, questions.questions[current.question]];
};

//for candidates/ employees
export const selectMyPendingForm = state => {
  let myforms = state.gift.giftForms.my;
  return myforms['Not declared yet'] ? myforms['Not declared yet'][0] : null;
};
export const selectMyPreviousForms = state => {
  const { Pass, Fail } = state.gift.giftForms.my;
  return { Pass, Fail };
};

//for reporting head /managers
export const selectJuniorPendingForms = state => {
  const { Pending, HR_approved = [], Manager_approved = [], Admin_approved = [] } = state.gift.giftForms.junior.pending;
  return { Pending, Approved: [...HR_approved, ...Manager_approved, ...Admin_approved] };
};
export const selectJuniorPreviousForms = state => {
  const { Pass, Fail } = state.gift.giftForms.junior.previous;
  return { Pass, Fail };
};

//for hr
export const selectAllPendingForms = state => {
  const { Manager_approved, Meeting_scheduled = [] } = state.gift.giftForms.all.pending;
  return { 'Scheduling required': Manager_approved, 'Scheduling completed': [...Meeting_scheduled] };
};
export const selectAllPreviousForms = state => {
  const { Pass, Fail } = state.gift.giftForms.all.previous;
  return { Pass, Fail };
};

//for admin
export const selectAllAdminPendingForms = state => {
  const { HR_approved = [] } = state.gift.giftForms.all.pending;
  return HR_approved;
};
export const selectAllAdminPreviousForms = state => {
  const { Pass, Fail } = state.gift.giftForms.all.previous;
  return { Pass, Fail };
};
export const selectCurrent = state => state.gift.current;
export const selectCompleted = state => state.gift.completed;
export const selectCurrentKras = state => state.gift.kras['current'];
export const selectDesiredKras = state => state.gift.kras['desired'];
export const selectBotTyping = state => state.gift.botTyping;
export const selectDisableInput = state => state.gift.disableInput;
export const selectMessages = state => state.gift.messages;
export const selectFullScreen = state => state.gift.fullScreen;
export const selectIsNext = state => state.gift.isNext;
export const selectIsStarted = state => state.gift.isStarted;
export const selectEditPreview = state => state.gift.editPreview;
export const selectDraft = state => state.gift.draft;
export const selectDesgination = state => ({
  current: state.gift.currentDesignationPreview,
  desired: state.gift.desiredDesignationPreview,
});
export const selectEligible = state => state.gift.eligible;
export const selectEligibleMessage = state => state.gift.eligibleMessage;
export const selectViewPrefilledForm = state => state.gift.viewPrefilledForm;
export const selectShowMyHistory = state => state.gift.showMyHistory;
export const selectShowSubmittedHistory = state => state.gift.showSubmittedHistory;
export const selectShowAdminHistory = state => state.gift.showAdminHistory;

export const selectDraftForm = state => state.gift.draftForm;
export const selectPreview = state => state.gift.preview;
export const selectUsingDraft = state => state.gift.usingDraft;
export const selectShowDraftMessage = state => {
  return state.gift.draftForm ? (state.gift.usingDraft ? false : true) : false;
};
export const selectJuryDetails = state => {
  return state.gift.juryDetails;
};
export const selectRejectReason = state => {
  return state.gift.reason;
};
export const selectEditForChanges = state => {
  return state.gift.editForChanges;
};
export const selectShowToolbar = state => {
  return state.gift.showToolbar;
};
export const selectGiftProgress = state => {
  const currentKras = state.gift.kras.current;
  const desiredKras = state.gift.kras.desired;
  const current = state.gift.preview.current;
  const desired = state.gift.preview.desired;

  let total = 0;
  for (let i = 0; i < currentKras.length; i++) {
    total += currentKras[i].questions.length;
  }
  for (let i = 0; i < desiredKras.length; i++) {
    total += desiredKras[i].questions.length;
  }

  let completed = 0;
  Object.keys(current).forEach(title => {
    completed += current[title].length;
  });
  Object.keys(desired).forEach(title => {
    completed += desired[title].length;
  });

  return Math.round((completed / total) * 100);
};

export const {
  setAnswer,
  nextQuestion,
  setReason,
  sendOutgoingMessage,
  setFullScreen,
  reset,
  setEditPreview,
  setDraftAnswer,
  setDraftReason,
  saveDraft,
  setKras,
  setGiftForms,
  setPreview,
  setShowHistory,
  setDraftForm,
  deleteDraft,
} = giftSlice.actions;
